import { useState, useEffect } from "react";
import { Table } from "antd";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate } from "react-router-dom";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../../../store/adminData/adminData";

import moment from "moment";
import {
  getPackages,
  setPackageSortOrder,
} from "../../../../../services/firebaseService/endPoints/admin/members";
import DraggableBodyRow from "../../../content/chants/DraggableBodyRow.jsx";
import { GripVertical } from "../../../../../utils/icons.jsx";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../../../services/firebaseService/connection";
import { getCurrencyUnicode } from "../../../../../helperFunctions/util";
import "./Packages.css";
import { EditOutlined } from "@ant-design/icons";
import ChantButton from "../../../../../CommonComponents/ChantButton/ChantButton";

const shippingOptions = [
  { value: "pickup", label: "Pick up only" },
  { value: "shipping", label: "Shipping only" },
  { value: "both", label: "Option of shipping or pickup" },
];

const MembershipPackageTable = (props) => {
  const navigate = useNavigate();
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];

  const [tableData, setTableData] = useState([]);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTransactionsFromPackages = async (packages) => {
    try {
      setIsLoading(true);
      const transactionsByPackage = {};

      const promises = packages.map(async (pkg) => {
        let dbQuery = query(
          collection(db, "transaction_record", currentGroup, "records"),
          where("packageId", "==", pkg?.id),
        );

        const snap = await getDocs(dbQuery);
        let transactions = [];
        for (let doc of snap.docs) {
          let data = doc.data();
          data.transactionId = doc.id;
          transactions.push(data);
        }

        const extractedData = transactions.reduce(
          (acc, curr) => {
            acc.totalAmount += curr.amount;
            if (curr?.subscribedProduct && curr?.subscribedProduct?.length > 0) {
              acc.totalRenewals += 1;
            }
            return acc;
          },
          { totalAmount: 0, totalRenewals: 0 },
        );

        transactionsByPackage[pkg.id] = {
          totalAmount: extractedData.totalAmount.toFixed(2),
          totalRenewals: extractedData.totalRenewals,
          totalSold: transactions?.length,
        };
        return transactions;
      });

      await Promise.all(promises);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  const fetchData = () => {
    getPackages(currentGroup).then((packages) => {
      setTableData(packages);
      fetchTransactionsFromPackages(packages);
    });
  };

  useEffect(() => {
    fetchData();
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveRow = (dragIndex, hoverIndex) => {
    const newData = JSON.parse(JSON.stringify(tableData));
    newData[dragIndex].sortIndex = hoverIndex + 1;
    let sortIndexChangeStep = hoverIndex - dragIndex > 0 ? -1 : 1;
    let i = sortIndexChangeStep > 0 ? hoverIndex : dragIndex + 1;
    let finalStep = sortIndexChangeStep < 0 ? hoverIndex : dragIndex - 1;
    for (; i <= finalStep; i++) {
      newData[i].sortIndex += sortIndexChangeStep;
    }
    setTableData(newData);
    setNeedsUpdate(true);
  };

  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  let groupCurrencyISO = adminData?.data?.paymentDetails
    ? adminData.data.paymentDetails.currency || "USD"
    : "USD";
  let groupCurrency = getCurrencyUnicode(groupCurrencyISO);

  const membershipPackageColumns = [
    {
      title: "Action",
      width: 100,
      className: "header-white",
      render: (data) => (
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img src={GripVertical} alt="*" />
          {data && data.id && (
            <EditOutlined
              onClick={() => navigate(`/admin/members/packages/edit/${data.id}`)}
            />
          )}
        </div>
      ),
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      className: "header-white",
      align: "center",
      render: (status) => status,
      width: 150,
    },
    {
      title: "Package",
      dataIndex: "name",
      className: "header-white",
      align: "center",
      width: 200,
      render: (name) => name,
    },
    {
      title: "Season",
      dataIndex: "season",
      className: "header-white",
      align: "center",
      render: (season) => season,
      width: 100,
    },
    {
      title: "Price",
      dataIndex: "price",
      className: "header-white",
      align: "center",
      width: 100,
      render: (price) => (price?.amount ? `${groupCurrency}${price?.amount}` : "-"),
    },
    {
      title: "Child Price",
      dataIndex: "childPrice",
      className: "header-white",
      width: 100,
      align: "center",
      render: (childPrice) => <span>{childPrice && childPrice.amount}</span>,
    },
    {
      title: "Package Expires",
      dataIndex: "packageExpiryDate",
      className: "header-white",
      width: 150,
      align: "center",
      render: (date) => {
        if (!date) return "-";
        let dateMoment = moment.unix(date);
        return `${dateMoment.month() + 1}/${dateMoment.date()}/${dateMoment.year()}`;
      },
    },
    {
      title: "Item choice?",
      dataIndex: "productChoice",
      className: "header-white",
      render: (choice) => choice,
      width: 200,
    },
    {
      title: "Offer Shipping?",
      dataIndex: "offerShipping",
      className: "header-white",
      render: (choice) => {
        let selectedOption = shippingOptions.find((option) => option.value === choice);
        return <span>{selectedOption ? selectedOption.label : ""}</span>;
      },
      width: 200,
    },
  ];

  let sortedData = [...tableData].sort((row1, row2) => row1.sortIndex - row2.sortIndex);
  let inactivePackages = sortedData.filter((pack) => pack.status === "inactive");
  let activePackages = sortedData.filter((pack) => pack.status !== "inactive");
  let dataSource = [...activePackages, ...inactivePackages];

  return (
    <>
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
        }}
      >
        <div style={{ display: "flex", gap: "8px" }}>
          {needsUpdate && (
            <ChantButton
              onClick={() => {
                let idOrderMap = tableData.reduce((acc, val, index) => {
                  acc[val.id] = val.sortIndex || index;
                  return acc;
                }, {});

                setPackageSortOrder(idOrderMap, currentGroup).then(() => {
                  setNeedsUpdate(false);
                });
              }}
            >
              Update Sort Changes
            </ChantButton>
          )}
        </div>
      </div>
      <div className="packages-table-container">
        <DndProvider backend={HTML5Backend}>
          <Table
            bordered
            rowKey={(record) => record.id}
            columns={membershipPackageColumns}
            className="table-backdrop dnd-table"
            dataSource={dataSource}
            onRow={(_, index) => ({
              index,
              moveRow,
            })}
            components={components}
            bodyStyle={{ backgroundColor: "#ffffff" }}
            loading={isLoading || props.membersLoading}
            pagination={{
              position: ["bottomLeft"],
              defaultPageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            scroll={{ x: "max-content" }}
          />
        </DndProvider>
      </div>
    </>
  );
};

export default MembershipPackageTable;
